import { Helmet } from "react-helmet-async";
import { useLang } from "../context/LanguageContext";

const SITE_URL = "https://www.taqueriadenina.com";
const SITE_NAME = "Nina's Taqueria";
const DEFAULT_IMAGE = "https://static.wixstatic.com/media/ea1028_15cb7cced9174b7e9dc5b3a9f73fd86e~mv2.jpg";

export default function SEO({
  title,
  titleEs,
  description,
  descriptionEs,
  path = "/",
  image = DEFAULT_IMAGE,
  type = "website",
  schema = null,
  keywords = null,
  keywordsEs = null,
}) {
  const { lang } = useLang();
  const isEs = lang === "es";
  const resolvedTitle = isEs && titleEs ? titleEs : title;
  const resolvedDesc  = isEs && descriptionEs ? descriptionEs : description;
  const resolvedKw    = isEs && keywordsEs ? keywordsEs : keywords;
  const suffix = isEs ? "Nina's Taqueria, Los Gatos CA" : `${SITE_NAME}, Los Gatos CA`;
  const defaultTitle = isEs
    ? `${SITE_NAME} — Tacos Mexicanos Auténticos en Los Gatos, CA`
    : `${SITE_NAME} — Authentic Mexican Tacos in Los Gatos, CA`;
  const fullTitle = resolvedTitle ? `${resolvedTitle} — ${suffix}` : defaultTitle;
  const url = `${SITE_URL}${path}`;
  const ogLocale = isEs ? "es_US" : "en_US";
  return (
    <Helmet prioritizeSeoTags>
      <html lang={isEs ? "es" : "en"} />
      <title>{fullTitle}</title>
      <meta name="description" content={resolvedDesc} />
      {resolvedKw && <meta name="keywords" content={resolvedKw} />}
      <link rel="canonical" href={url} />

      {/* hreflang — indexable EN + ES variants of the same page (query param) */}
      <link rel="alternate" hrefLang="en" href={url} />
      <link rel="alternate" hrefLang="es" href={`${url}?lang=es`} />
      <link rel="alternate" hrefLang="x-default" href={url} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={resolvedDesc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta property="og:locale" content={ogLocale} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={resolvedDesc} />
      <meta name="twitter:image" content={image} />

      {/* Robots */}
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <meta name="googlebot" content="index, follow" />

      {schema && <script type="application/ld+json">{JSON.stringify(schema)}</script>}
    </Helmet>
  );
}

// Ready-made JSON-LD schemas ----------------------------------------------

export const restaurantSchema = {
  "@context": "https://schema.org",
  "@type": "Restaurant",
  name: "Nina's Taqueria",
  alternateName: ["Taqueria de Nina", "Taquería de Nina", "Nina Taqueria"],
  image: [
    "https://static.wixstatic.com/media/ea1028_15cb7cced9174b7e9dc5b3a9f73fd86e~mv2.jpg",
    "https://static.wixstatic.com/media/ea1028_f5c44db7d9d740c5b83569561a6afd3b~mv2.jpg",
    "https://static.wixstatic.com/media/ea1028_23d325092fb44ffb9fc97df3cc5aa65e~mv2.jpg",
  ],
  logo: "https://customer-assets.emergentagent.com/job_taco-den-update/artifacts/fkec42i9_image.png",
  description:
    "Family-owned traditional Mexican taqueria in Los Gatos, California. Handmade tortillas, fresh salsas, and authentic recipes served daily.",
  url: SITE_URL,
  telephone: "+1-408-402-5076",
  email: "info@taqueriadenina.com",
  priceRange: "$$",
  servesCuisine: ["Mexican", "Tex-Mex", "Latin American"],
  address: {
    "@type": "PostalAddress",
    streetAddress: "49 E Main Street",
    addressLocality: "Los Gatos",
    addressRegion: "CA",
    postalCode: "95030",
    addressCountry: "US",
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: 37.2266,
    longitude: -121.9747,
  },
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
      opens: "10:00",
      closes: "20:00",
    },
  ],
  acceptsReservations: "False",
  hasMenu: `${SITE_URL}/menu`,
  paymentAccepted: "Cash, Credit Card, Debit Card",
  // Note: aggregateRating is intentionally NOT emitted on our own site's
  // Restaurant / LocalBusiness JSON-LD. Google explicitly considers self-serving
  // LocalBusiness/Organization review markup ineligible for review-star rich
  // results, and third-party ratings (e.g. Google Reviews) must not be
  // aggregated back into our own structured data.
  sameAs: [
    "https://order.taqueriadenina.com/",
    "https://www.toasttab.com/nina-taqueria-los-gatos/",
    "https://www.google.com/maps?cid=12270814832204351491",
    "https://www.instagram.com/taqueriadeninas/",
    "https://www.facebook.com/taqueriadenina",
    "https://www.yelp.com/biz/ninas-taqueria-los-gatos",
  ],
};

export const breadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: item.name,
    item: `${SITE_URL}${item.path}`,
  })),
});

export const menuSchema = (menuItems, menuCategories) => ({
  "@context": "https://schema.org",
  "@type": "Menu",
  name: "Nina's Taqueria Menu",
  hasMenuSection: menuCategories.map((cat) => ({
    "@type": "MenuSection",
    name: cat.name,
    hasMenuItem: menuItems
      .filter((i) => i.category === cat.id)
      .map((i) => ({
        "@type": "MenuItem",
        name: i.name,
        description: i.description,
        offers: {
          "@type": "Offer",
          price: i.price,
          priceCurrency: "USD",
        },
        ...(i.image ? { image: i.image } : {}),
      })),
  })),
});

export const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "@id": `${SITE_URL}/#localbusiness`,
  name: "Nina's Taqueria",
  image: "https://customer-assets.emergentagent.com/job_taco-den-update/artifacts/fkec42i9_image.png",
  url: SITE_URL,
  telephone: "+1-408-402-5076",
  priceRange: "$$",
  address: {
    "@type": "PostalAddress",
    streetAddress: "49 E Main Street",
    addressLocality: "Los Gatos",
    addressRegion: "CA",
    postalCode: "95030",
    addressCountry: "US",
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: 37.2266,
    longitude: -121.9747,
  },
  areaServed: [
    { "@type": "City", name: "Los Gatos" },
    { "@type": "City", name: "Saratoga" },
    { "@type": "City", name: "Campbell" },
    { "@type": "City", name: "Monte Sereno" },
    { "@type": "City", name: "San Jose" },
  ],
  openingHoursSpecification: [{
    "@type": "OpeningHoursSpecification",
    dayOfWeek: ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"],
    opens: "10:00",
    closes: "20:00",
  }],
  // aggregateRating intentionally omitted — see restaurantSchema comment above.
  sameAs: [
    "https://www.google.com/maps?cid=12270814832204351491",
    "https://www.instagram.com/taqueriadeninas/",
    "https://www.facebook.com/taqueriadenina",
    "https://www.yelp.com/biz/ninas-taqueria-los-gatos",
  ],
};

export const faqSchema = (items, lang = "en") => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: items.map((f) => ({
    "@type": "Question",
    name: lang === "es" ? (f.qEs || f.q) : f.q,
    acceptedAnswer: { "@type": "Answer", text: lang === "es" ? (f.aEs || f.a) : f.a },
  })),
});

export const cateringServiceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  "@id": `${SITE_URL}/catering#service`,
  serviceType: "Event Catering",
  name: "Mexican Catering by Nina's Taqueria",
  description:
    "Authentic Mexican catering for weddings, corporate lunches, birthdays and private events in Los Gatos and the South Bay. Taco bars, burrito & bowl trays, and family-style spreads with handmade tortillas and fresh salsas. Order online for an instant quote.",
  url: `${SITE_URL}/catering`,
  provider: {
    "@type": "Restaurant",
    name: "Nina's Taqueria",
    telephone: "+1-408-402-5076",
    url: SITE_URL,
    address: {
      "@type": "PostalAddress",
      streetAddress: "49 E Main Street",
      addressLocality: "Los Gatos",
      addressRegion: "CA",
      postalCode: "95030",
      addressCountry: "US",
    },
  },
  areaServed: [
    { "@type": "City", name: "Los Gatos" },
    { "@type": "City", name: "Saratoga" },
    { "@type": "City", name: "Campbell" },
    { "@type": "City", name: "Monte Sereno" },
    { "@type": "City", name: "San Jose" },
    { "@type": "City", name: "Cupertino" },
    { "@type": "City", name: "Santa Clara" },
  ],
  offers: {
    "@type": "Offer",
    url: "https://taqueriadenina.gethoneycart.com/",
    availability: "https://schema.org/InStock",
    priceCurrency: "USD",
  },
};

// NOTE: A `reviewsSchema()` helper used to live here that emitted
// aggregateRating + Review objects from our own testimonials array. It has
// been intentionally removed because Google explicitly deprecates self-serving
// review markup on LocalBusiness/Restaurant/Organization pages — such markup
// is ineligible for review-star rich results. If we ever need review markup
// again, it must ONLY be attached to specific Products/Menu items that were
// independently reviewed, never to the business itself.
